/**
 * /accounts/:aid/voice — Voice settings + dry-run preview.
 *
 * Three sections (top to bottom):
 *   1. Preview: date + slider → debounced server-rendered text →
 *      Listen button drives the audio element.
 *   2. Voice: dropdown + speaking-rate slider + pitch slider.
 *      Admin-only edit; non-admins see read-only.
 *   3. Advanced (disclosure): phrase editors grouped into "What the
 *      button says when pressed", "Bedtime & night-time", and
 *      "Notifications" (reminder + morning nudge). The elder's real
 *      name is shown in place of the {name} token and converted back
 *      on save (phraseToDisplay / phraseToStored) so caregivers never
 *      see templating syntax. Admin-only edit; preview reflects
 *      in-flight edits immediately.
 *
 * # Saving: per-field, on change — no Save button
 *
 * Every control saves itself: discrete controls (checkboxes, the voice
 * dropdown) write immediately; sliders write once per gesture (debounced);
 * phrase boxes write on blur (with a debounce fallback so "Reset to
 * default" also lands). Each write patches ONLY its own field, so two
 * caregivers (or two tabs) editing different things can never clobber
 * each other, and what the page shows always converges on what's saved.
 *
 * Two hard-won rules encoded here — both earlier designs failed without
 * them (#122's auto-save write-loop; the explicit-Save stale-tab clobber):
 *   1. Writes originate ONLY from user-event handlers (via
 *      useDebouncedCommit / useAccountAutosave) — never from effects
 *      observing state, so a Firestore snapshot can never trigger a write.
 *   2. No whole-form writes — a save carries exactly the changed field.
 *
 * Account writes fire the server's TTS-regen trigger, so the debounce
 * choices are cost decisions, not just UX: one write per slider gesture,
 * one per phrase edit.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatInTimeZone } from 'date-fns-tz';

import { useAccount } from '@/hooks/useAccount';
import { useAccountAutosave } from '@/hooks/useAccountAutosave';
import { useCalendars } from '@/hooks/useCalendars';
import { useDebouncedCommit } from '@/hooks/useDebouncedCommit';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useEvents } from '@/hooks/useEvents';
import { useMyRole } from '@/hooks/useMyRole';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { Spinner } from '@/components/ui/Spinner';
import { Textarea } from '@/components/ui/Textarea';
import { previewSummary } from '@/lib/callables';
import { playClipsSequentially } from '@/lib/playClipsSequentially';
import { track } from '@/lib/analytics';
import { safeTimeZone, wallClockToInstant } from '@/lib/timeZone';
import { phraseToDisplay, phraseToStored } from '@/lib/phraseName';
import { phrasePatchFromEdits } from '@/lib/phraseDefaults';
import {
  BEDTIME_HOUR,
  CURATED_VOICES,
  DEFAULT_VOICE,
  PITCH,
  SPEAKING_RATE,
  WAKE_HOUR,
  formatHour,
} from '@/lib/voices';
import type { Phrases } from '@/schemas';

// Hours displayed as "9pm–3am". Crossing midnight is fine — the night
// phrases naturally span midnight depending on bedtime / wake-up.
function hourRange(fromHour: number, toHour: number): string {
  return `${formatHour(fromHour)}–${formatHour(toHour)}`;
}

interface PhraseField {
  key: keyof Phrases;
  label: string;
  /** When does this phrase fire? Computed from wake/bedtime values. */
  intervalLabel: (wake: number, bed: number) => string;
  placeholder: string;
  hint?: string;
}

// Personality phrases — independent of wake/bedtime. Greeting + the
// no-events fallbacks live here.
const PHRASE_FIELDS_GENERAL: PhraseField[] = [
  {
    key: 'greeting',
    label: 'Greeting',
    intervalLabel: () => 'Top of every press',
    placeholder: 'Hello {name}.',
  },
  {
    key: 'noEventsMorning',
    label: 'No events — morning',
    intervalLabel: (wake) => hourRange(wake, 12),
    placeholder: 'You have nothing planned today. It’s a nice relaxed day!',
  },
  {
    key: 'noEventsAfternoon',
    label: 'No events — afternoon',
    intervalLabel: (_, bed) => hourRange(12, bed - 2),
    placeholder: 'Nothing else planned today.',
  },
];

// The reminder-notification prompt. Lives with the morning notification in the
// "Notifications" card (both flash the button + optionally speak on a loop),
// not with the press-triggered phrases above.
const NOTIFICATION_PROMPT_FIELD: PhraseField = {
  key: 'notificationPrompt',
  label: 'Reminder prompt',
  intervalLabel: () => 'When a reminder pops up',
  placeholder: 'Hello {name}, press the button for a reminder',
  hint: 'Played on a loop while a reminder is active, until the button is pressed or the event ends. Leave it blank for a silent flash (no voice). You can also add {title} for the event’s name or {time} for its clock time.',
};

// Time-orientation phrases — fire relative to the configured wake /
// bedtime hours. Bedtime nudges + nighttime sleep nudges, ordered
// chronologically from early evening through to just before wake.
const PHRASE_FIELDS_TIME: PhraseField[] = [
  {
    key: 'bedtimeEarly',
    label: 'Bedtime nudge — early',
    intervalLabel: (_, bed) => hourRange(bed - 2, bed - 1),
    placeholder: 'Bed time in a couple of hours.',
  },
  {
    key: 'bedtimeLate',
    label: 'Bedtime nudge — late',
    intervalLabel: (_, bed) => hourRange(bed - 1, bed),
    placeholder: 'Bed time soon.',
  },
  {
    key: 'nightFar',
    label: 'Night — earlier in the night',
    intervalLabel: (wake, bed) => hourRange(bed, (wake - 3 + 24) % 24),
    placeholder: 'There are about {hours} hours until morning. Try to get some rest.',
    hint: 'Use {hours} for the count.',
  },
  {
    key: 'nightClose',
    label: 'Night — within 3 hrs of waking',
    intervalLabel: (wake) => hourRange((wake - 3 + 24) % 24, (wake - 1 + 24) % 24),
    placeholder: 'Morning is only a couple of hours away. Try to get some rest.',
  },
  {
    key: 'nightVeryClose',
    label: 'Night — within 1 hr of waking',
    intervalLabel: (wake) => hourRange((wake - 1 + 24) % 24, wake),
    placeholder: 'It will be morning very soon. Try to get some rest.',
  },
];

// The morning-nudge prompt. Rendered in the "Morning nudge" half of the
// Notifications card (gated by the enable toggle), reusing the same
// PhraseInput + Phrases storage. Only {name} — no event vars.
const MORNING_PROMPT_FIELD: PhraseField = {
  key: 'morningPrompt',
  label: 'Morning prompt',
  intervalLabel: (wake) => `From ${formatHour(wake)}, until pressed`,
  placeholder: 'Good morning {name}. Press the button to hear today’s plan.',
  hint: 'Played on a loop from wake-up until the button is pressed (then the day’s summary plays) or two hours pass. Leave it blank for a silent nudge — the button just flashes.',
};

// Storage-form default for every phrase field, keyed by phrase key. Derived
// from the field lists so it can't drift from the placeholders the inputs show.
// A phrase equal to its default is stripped before saving (an at-default
// phrase is equivalent to unset) — see stripDefaultPhrases.
const PHRASE_DEFAULTS: Record<string, string> = Object.fromEntries(
  [
    ...PHRASE_FIELDS_GENERAL,
    NOTIFICATION_PROMPT_FIELD,
    ...PHRASE_FIELDS_TIME,
    MORNING_PROMPT_FIELD,
  ].map((f) => [f.key, f.placeholder]),
);

/** The phrases edit buffer: the full map for display, plus which keys
 * this tab actually touched (the only ones a commit writes). */
interface PhraseEdits {
  phrases: Phrases;
  edited: Array<keyof Phrases>;
}

function minutesToHHMM(m: number): string {
  const hh = Math.floor(m / 60).toString().padStart(2, '0');
  const mm = (m % 60).toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

// Spoken summary rounds to the nearest 5 minutes (see
// spoken_summary.py — `int((minute + 2.5) / 5) * 5`). Match that
// here so the displayed preview time agrees with what's spoken.
const PREVIEW_STEP_MIN = 5;
function snapToPreviewGrid(m: number): number {
  return Math.round(m / PREVIEW_STEP_MIN) * PREVIEW_STEP_MIN;
}

function todayLocalISO(tz: string): string {
  return formatInTimeZone(new Date(), tz, 'yyyy-MM-dd');
}

function buildAtIso(date: string, minutes: number, tz: string): string {
  // Interpret the chosen preview time as wall-clock in the elder's tz, then
  // format the resulting instant back with that zone's offset. date-fns-tz
  // (via wallClockToInstant) handles DST — verified byte-identical to the
  // previous hand-rolled offset math across normal + DST-boundary cases.
  const instant = wallClockToInstant(date, minutesToHHMM(minutes), tz);
  return formatInTimeZone(instant, tz, "yyyy-MM-dd'T'HH:mm:ssXXX");
}

// Debounce tiers for the per-field autosave. Discrete controls commit on
// the next tick (buffer gives instant visual feedback + revert-on-failure);
// sliders commit once per gesture; phrase boxes mostly commit on blur, the
// debounce is the fallback for edits that never blur (Reset to default).
const COMMIT_IMMEDIATE_MS = 0;
const COMMIT_SLIDER_MS = 800;
const COMMIT_PHRASES_MS = 1500;

// Voice is a "carry-over" section, so switching elders navigates
// /accounts/A/voice -> /accounts/B/voice without React remounting the page.
// This wrapper keys the inner page on `aid` to force a fresh mount per
// account, so per-field edit buffers and the mount-seeded preview state
// reset. Without it the new elder's page could briefly show — and a pending
// debounced commit could write — the previous elder's values.
export function VoicePage() {
  const { aid } = useParams<{ aid: string }>();
  return <VoicePageInner key={aid ?? 'no-account'} />;
}

function VoicePageInner() {
  const { aid } = useParams<{ aid: string }>();
  const location = useLocation();
  const claimState = (location.state ?? null) as { justClaimed?: boolean; room?: string } | null;
  const [justClaimedBanner, setJustClaimedBanner] = useState<{ room: string } | null>(
    claimState?.justClaimed ? { room: claimState.room ?? '' } : null,
  );
  // Drop the banner state from history so a refresh / back-nav doesn't
  // resurrect it after the caregiver has already seen it.
  useEffect(() => {
    if (claimState?.justClaimed) {
      window.history.replaceState({}, '');
    }
  }, [claimState?.justClaimed]);
  const { account, loading: accountLoading } = useAccount(aid ?? null);
  const { calendars, loading: calendarsLoading } = useCalendars(aid ?? null);
  const primaryCalendarId = calendars[0]?.id ?? null;
  const role = useMyRole(aid ?? null);
  const isAdmin = role === 'admin';
  const tz = safeTimeZone(account?.timeZone);

  // Live events subscription — for preview FRESHNESS, not rendering. The
  // server renders the preview from the materialised event cache (device
  // parity), which is rebuilt asynchronously after an event edit; a preview
  // fetched inside that window shows the pre-edit event. Two jobs here:
  //  1. an events fingerprint in the preview key, so an edit made in
  //     another tab (or just before navigating back) refetches;
  //  2. `eventsSyncedAtMs` sent to the server as a cache-freshness floor —
  //     the server falls back to live event reads when its cache predates
  //     what this client has already seen, making the refetch deterministic
  //     instead of racing the rebuild.
  const { events, loading: eventsLoading } = useEvents(aid ?? null, primaryCalendarId);
  const eventsFingerprint = useMemo(
    () =>
      events
        .map((e) => `${e.id}:${(e.updatedAt as { toMillis?: () => number } | undefined)?.toMillis?.() ?? 0}`)
        .join('|'),
    [events],
  );
  const maxEventUpdatedAtMs = useMemo(
    () =>
      events.reduce(
        (max, e) =>
          Math.max(max, (e.updatedAt as { toMillis?: () => number } | undefined)?.toMillis?.() ?? 0),
        0,
      ),
    [events],
  );
  // Wall-clock stamp for changes that move no updatedAt forward — a delete,
  // or a write whose serverTimestamp hasn't acked yet (reads as null). Only
  // those need the local clock; an EDIT's committed updatedAt is the better
  // floor (server-authoritative, and it lets the post-rebuild cache satisfy
  // the floor again instead of pinning the session to live reads). On the
  // INITIAL snapshot, a pending-ack write (null updatedAt) also stamps, so
  // edit-then-quickly-navigate-here still previews fresh. Residual:
  // delete-then-navigate-here can still serve one stale preview (nothing
  // observable distinguishes it from a plain visit).
  const lastEventsChangeStampRef = useRef(0);
  const prevEventsFingerprintRef = useRef<string | null>(null);
  const prevMaxUpdatedAtRef = useRef(0);
  const hasPendingWrite = events.some(
    (e) => ((e.updatedAt as { toMillis?: () => number } | undefined)?.toMillis?.() ?? 0) === 0,
  );
  useEffect(() => {
    if (eventsLoading) return;
    const prevFp = prevEventsFingerprintRef.current;
    if (prevFp === null) {
      if (hasPendingWrite) lastEventsChangeStampRef.current = Date.now();
    } else if (prevFp !== eventsFingerprint && maxEventUpdatedAtMs <= prevMaxUpdatedAtRef.current) {
      lastEventsChangeStampRef.current = Date.now();
    }
    prevEventsFingerprintRef.current = eventsFingerprint;
    prevMaxUpdatedAtRef.current = maxEventUpdatedAtMs;
  }, [eventsFingerprint, eventsLoading, maxEventUpdatedAtMs, hasPendingWrite]);
  const eventsSyncedAtMs = Math.max(maxEventUpdatedAtMs, lastEventsChangeStampRef.current);

  // Preview "now" — date + minutes. The caregiver almost always wants to
  // preview against the *current* time when they open the page.
  const [date, setDate] = useState<string>(() => todayLocalISO(tz));
  const [minutes, setMinutes] = useState<number>(() => {
    const hhmm = formatInTimeZone(new Date(), tz, 'HH:mm').split(':');
    return snapToPreviewGrid(Number(hhmm[0]) * 60 + Number(hhmm[1] ?? '0'));
  });
  // Defensive: normalise a seed that landed off the 5-minute grid
  // (e.g. after a hot reload).
  useEffect(() => {
    const snapped = snapToPreviewGrid(minutes);
    if (snapped !== minutes) setMinutes(snapped);
  }, [minutes, setMinutes]);

  // ---- Per-field autosave --------------------------------------------
  //
  // Each control renders the live doc value (or its own uncommitted
  // buffer while an edit is in flight) and saves just its own field.
  // External changes — another tab, another caregiver — flow straight
  // into the controls via the account snapshot: there is no draft state
  // to go stale and no whole-form write to clobber anyone.

  const { save, savePhrases, status: saveStatus } = useAccountAutosave(aid ?? null, isAdmin);

  const voiceField = useDebouncedCommit<string>(
    account?.voice || DEFAULT_VOICE,
    (v) => save({ voice: v }),
    COMMIT_IMMEDIATE_MS,
  );
  const rateField = useDebouncedCommit<number>(
    account?.speakingRate ?? SPEAKING_RATE.default,
    (v) => save({ speakingRate: v }),
    COMMIT_SLIDER_MS,
  );
  const pitchField = useDebouncedCommit<number>(
    account?.pitch ?? PITCH.default,
    (v) => save({ pitch: v }),
    COMMIT_SLIDER_MS,
  );
  const wakeField = useDebouncedCommit<number>(
    account?.wakeHour ?? WAKE_HOUR.default,
    (v) => save({ wakeHour: v }),
    COMMIT_SLIDER_MS,
  );
  const bedtimeField = useDebouncedCommit<number>(
    account?.bedtimeHour ?? BEDTIME_HOUR.default,
    (v) => save({ bedtimeHour: v }),
    COMMIT_SLIDER_MS,
  );
  const nudgeField = useDebouncedCommit<boolean>(
    account?.morningNudgeEnabled ?? false,
    (v) => save({ morningNudgeEnabled: v }),
    COMMIT_IMMEDIATE_MS,
  );
  const use24Field = useDebouncedCommit<boolean>(
    account?.use24HourTime ?? false,
    (v) => save({ use24HourTime: v }),
    COMMIT_IMMEDIATE_MS,
  );
  // Phrase edits buffer the whole map for display (the inputs and the
  // preview read it) but COMMIT only the keys the caregiver touched, as
  // per-key dot-path writes — field-granular like every other control,
  // so two devices editing DIFFERENT phrases can't overwrite each other.
  // An at-default (or default-restored) value commits as a key-delete:
  // absent-on-the-doc means "use the default".
  const phrasesField = useDebouncedCommit<PhraseEdits>(
    { phrases: account?.phrases ?? {}, edited: [] },
    (v) => {
      const patch = phrasePatchFromEdits(v.phrases, v.edited, PHRASE_DEFAULTS);
      return Object.keys(patch).length > 0 ? savePhrases(patch) : Promise.resolve();
    },
    COMMIT_PHRASES_MS,
  );
  const phrasesShown = phrasesField.value.phrases;
  const setPhrase = (key: keyof Phrases, v: string) => {
    // The textarea's maxLength=200 bounds the DISPLAY form, but the doc
    // stores the {name}-token form, which is LONGER when the elder's name is
    // shorter than "{name}" ("Ruth" → +2 per mention) — and firestore.rules
    // caps the STORED form at 200, so an over-long stored value would be
    // rejected server-side (and the rejection reverts the whole edit
    // buffer). Block the keystroke instead: typing just stops a few
    // characters earlier for short names. Measured in UTF-8 BYTES — at
    // least as strict as either plausible rules size() semantics (code
    // units or bytes), so "é"/emoji phrases can't slip past a
    // units-based guard and still be rejected server-side. A SHRINKING
    // edit is always allowed, so a legacy over-long value can still be
    // edited down (else even deleting a character would be swallowed).
    const bytes = (s: string) => new TextEncoder().encode(s).length;
    if (bytes(v) > 200 && bytes(v) >= bytes(phrasesField.value.phrases[key] ?? '')) return;
    const cur = phrasesField.value;
    phrasesField.set({
      phrases: { ...cur.phrases, [key]: v },
      edited: cur.edited.includes(key) ? cur.edited : [...cur.edited, key],
    });
  };

  const atIso = useMemo(
    () => buildAtIso(date, minutes, tz),
    [date, minutes, tz],
  );

  // Debounced bundle so a slider scrub fires one preview at the end. Uses the
  // same values the controls render (buffer-or-doc), so unsaved in-flight
  // edits preview immediately. Includes the morning-nudge toggle + wake hour
  // so the server-computed morning annotation refreshes when they change,
  // and the events fingerprint so a calendar edit refetches the preview.
  const debouncedPreviewKey = useDebouncedValue(
    JSON.stringify({
      atIso,
      phrases: phrasesShown,
      voice: voiceField.value,
      rate: rateField.value,
      pitch: pitchField.value,
      morningNudge: nudgeField.value,
      wake: wakeField.value,
      use24Hour: use24Field.value,
      events: eventsFingerprint,
    }),
    250,
  );

  const previewQuery = useQuery({
    queryKey: ['preview', aid, debouncedPreviewKey],
    queryFn: async () => {
      if (!aid) return { text: '', activeNotifications: [] };
      const res = await previewSummary({
        accountId: aid,
        atIso,
        withAudio: false,
        phrasesOverride: hasAnyPhrase(phrasesShown) ? phrasesShown : undefined,
        morningNudgeEnabled: nudgeField.value,
        wakeHour: wakeField.value,
        use24HourTime: use24Field.value,
        eventsSyncedAtMs,
      });
      return res.data;
    },
    // Gate on the CALENDARS + events snapshots so the first fetch already
    // carries the real fingerprint + syncedAt. Without the calendars gate,
    // render 1 has cid=null → useEvents reports loading=false with an empty
    // list → an ungated fetch #1 fires floorless (can flash the stale title
    // this branch exists to fix) and fetch #2 follows when the snapshot
    // lands — double the invocations per visit.
    enabled: Boolean(aid) && !calendarsLoading && !eventsLoading,
    staleTime: 30_000,
    // Refetch on every mount (each navigation back to Voice) so the summary
    // reflects fresh events even when the fingerprint is unchanged;
    // staleTime still dedupes in-session slider scrubs.
    refetchOnMount: 'always',
    placeholderData: (prev) => prev,
  });

  // ---- Listen button ------------------------------------------------

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [listenBusy, setListenBusy] = useState(false);
  const [notifListenBusy, setNotifListenBusy] = useState(false);
  const [listenError, setListenError] = useState<string | null>(null);

  // What the device would emit at the preview moment, driving the annotation
  // and Listen button below (both hide when nothing's active). Resolved entirely
  // server-side (preview_summary), which returns the active notification — event
  // reminder or morning nudge — using the same precedence, window logic, and
  // phrase templates the device uses, from the toggle/wake-hour/phrases we
  // send it. We just render it: `spokenText` empty ⇒ silent flash (LED only);
  // `kind` drives the Listen synth request.
  const previewNotif:
    | { items: ActiveNotificationItem[]; spokenText: string | null; kind: 'event' | 'morning' }
    | null =
    (() => {
      const items = previewQuery.data?.activeNotifications ?? [];
      const first = items[0];
      if (!first) return null;
      // Gate the Listen button on the SAME item the annotation voices (items[0],
      // the nearest one the device plays), so button, annotation, and synth all
      // agree. Empty ⇒ silent, so the button hides.
      return {
        items,
        spokenText: first.spokenText || null,
        kind: first.kind,
      };
    })();

  function playNotificationUrl(url: string) {
    if (!audioRef.current) return;
    audioRef.current.src = url;
    void audioRef.current.play().catch(() => {
      // Some browsers reject programmatic playback; leave the audio
      // element ready for a manual press of the play control.
    });
  }

  async function onListen() {
    if (!aid) return;
    setListenBusy(true);
    setListenError(null);
    try {
      const res = await previewSummary({
        accountId: aid,
        atIso,
        withAudio: true,
        phrasesOverride: hasAnyPhrase(phrasesShown) ? phrasesShown : undefined,
        voiceOverride: voiceField.value,
        speakingRateOverride: rateField.value,
        pitchOverride: pitchField.value,
        eventsSyncedAtMs,
        // Match the text preview: honour an in-flight 24-hour edit so the
        // audio doesn't speak the saved format while the text shows the edit.
        use24HourTime: use24Field.value,
      });
      track('voice_preview_played');
      // Play the SAME two clips the device does — greeting then body —
      // in sequence, so the preview is byte-faithful to the button.
      // Fire-and-forget: the spinner tracks the fetch above (the slow part,
      // where the server synthesises), NOT the playback — so a stalled clip
      // can never strand `listenBusy`.
      const { greetingUrl, bodyUrl } = res.data;
      if (audioRef.current) {
        playClipsSequentially(audioRef.current, [greetingUrl, bodyUrl]);
      }
    } catch (e) {
      setListenError(e instanceof Error ? e.message : 'Could not generate audio.');
    } finally {
      setListenBusy(false);
    }
  }

  async function onListenNotification(kind: 'event' | 'morning') {
    if (!aid) return;
    setNotifListenBusy(true);
    setListenError(null);
    try {
      // The server derives the reminder phrase and synthesises it at the LIVE
      // voice settings (like the summary Listen), so the preview tracks the
      // sliders instead of playing the last-saved clip. We send the current
      // phrase values so in-flight prompt edits are reflected.
      const res = await previewSummary({
        accountId: aid,
        atIso,
        withAudio: false,
        notificationPreview: kind,
        phrasesOverride: hasAnyPhrase(phrasesShown) ? phrasesShown : undefined,
        voiceOverride: voiceField.value,
        speakingRateOverride: rateField.value,
        pitchOverride: pitchField.value,
        // Honour an in-flight 24-hour edit so a {time} in the prompt is
        // spoken in the shown format (matches the annotation/text preview).
        use24HourTime: use24Field.value,
        eventsSyncedAtMs,
      });
      track('voice_preview_played');
      const url = res.data.notificationAudioUrl;
      if (url) playNotificationUrl(url);
    } catch (e) {
      setListenError(e instanceof Error ? e.message : 'Could not generate audio.');
    } finally {
      setNotifListenBusy(false);
    }
  }

  // "Advanced" disclosure for Phrases + Time-orientation cards.
  // Always starts closed — the whole point of the section is to keep
  // the default page short. Existing customisations are still
  // reflected in the preview output above, so caregivers don't need
  // the input fields visible to verify their saved values.
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);

  function onResetVoice() {
    // Route the reset through the same field buffers as manual edits so a
    // pending in-flight edit (e.g. a just-scrubbed rate) can't commit AFTER
    // the reset and silently undo it.
    voiceField.set(DEFAULT_VOICE);
    rateField.set(SPEAKING_RATE.default);
    pitchField.set(PITCH.default);
    wakeField.set(WAKE_HOUR.default);
    bedtimeField.set(BEDTIME_HOUR.default);
  }

  if (accountLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }
  if (!account) {
    return <div className="text-sm text-ink-muted">Account not found.</div>;
  }

  const elderName = account.elderName || 'this person';

  return (
    <div className="mx-auto max-w-3xl space-y-6 pb-36 xl:pb-28">
      <h1 className="text-2xl font-semibold text-ink">Voice for {elderName}</h1>

      <p className="text-sm text-ink-soft">
        See exactly what would be spoken at any moment, listen to it, and tune the voice
        and phrasing on this page — based on{' '}
        {primaryCalendarId ? (
          <Link
            to={`/accounts/${aid}/calendars/${primaryCalendarId}/events`}
            className="text-accent-700 underline-offset-2 hover:underline"
          >
            {elderName}'s calendar
          </Link>
        ) : (
          <span>{elderName}'s calendar</span>
        )}
        . Changes save automatically.
      </p>

      {justClaimedBanner && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold">You're all set! 🎉</p>
              <p className="mt-1">
                {elderName}'s button{justClaimedBanner.room ? ` in the ${justClaimedBanner.room}` : ''} is
                ready. Press it any time to hear today's summary. Use this page to tune the voice
                and what gets said.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setJustClaimedBanner(null)}
              className="shrink-0 rounded-md px-2 py-1 text-emerald-900 hover:bg-emerald-100"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {!isAdmin && (
        <div className="rounded-md bg-paper-alt px-3 py-2 text-sm text-ink-soft">
          Only admins can change voice settings. You can still preview.
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Date" htmlFor="preview-date">
              <Input
                id="preview-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </Field>
            <Field
              label={`Time — ${minutesToHHMM(minutes)}`}
              htmlFor="preview-time"
            >
              <Slider
                id="preview-time"
                min={0}
                max={1439}
                step={5}
                value={minutes}
                onChange={setMinutes}
                aria-label="Preview time"
              />
            </Field>
          </div>

          <ActiveNotificationsBlock notifications={previewNotif?.items} />

          <div className="rounded-md border border-paper-border bg-paper px-3 py-3 text-sm text-ink">
            <p className="mb-1 text-xs font-bold">
              When pressed, the button would speak:
            </p>
            <div className="min-h-12 whitespace-pre-wrap">
            {previewQuery.isFetching && !previewQuery.data ? (
              <Spinner />
            ) : previewQuery.error ? (
              <span className="text-red-700">
                {previewQuery.error instanceof Error
                  ? previewQuery.error.message
                  : 'Could not render text.'}
              </span>
            ) : (
              previewQuery.data?.text || ' '
            )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={onListen} disabled={listenBusy}>
              {listenBusy ? 'Generating…' : 'Listen'}
            </Button>
            {(() => {
              // Show the Listen button only when a notification (event reminder
              // or morning nudge) is actually active at the preview time and
              // isn't silent. Nothing firing, or a cleared (silent) prompt, → no
              // button; the annotation above shows "flash silently — no voice".
              const notif = previewNotif;
              if (!notif || !notif.spokenText) return null;
              return (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={notifListenBusy}
                  onClick={() => void onListenNotification(notif.kind)}
                  title="Play the notification audio the button would use at this time, at the current voice settings."
                >
                  {notifListenBusy ? 'Generating…' : 'Listen to the prompt'}
                </Button>
              );
            })()}
            {listenError && (
              <span className="text-xs text-red-700">{listenError}</span>
            )}
            <audio ref={audioRef} className="ml-auto" controls preload="none" />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Voice</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <Field label="Voice" htmlFor="voice-pick">
            <select
              id="voice-pick"
              className="h-10 w-full rounded-lg border border-paper-border bg-white px-3 text-sm text-ink"
              value={voiceField.value}
              onChange={(e) => voiceField.set(e.target.value)}
              disabled={!isAdmin}
            >
              {CURATED_VOICES.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label={`Speaking rate — ${rateField.value.toFixed(2)}×`}
            htmlFor="rate"
          >
            <Slider
              id="rate"
              min={SPEAKING_RATE.min}
              max={SPEAKING_RATE.max}
              step={SPEAKING_RATE.step}
              value={rateField.value}
              onChange={rateField.set}
              disabled={!isAdmin}
              aria-label="Speaking rate"
            />
          </Field>

          <Field
            label={`Pitch — ${pitchField.value >= 0 ? '+' : ''}${pitchField.value.toFixed(1)}`}
            htmlFor="pitch"
          >
            <Slider
              id="pitch"
              min={PITCH.min}
              max={PITCH.max}
              step={PITCH.step}
              value={pitchField.value}
              onChange={pitchField.set}
              disabled={!isAdmin}
              aria-label="Pitch"
            />
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Day length</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="text-xs text-ink-muted">
            The bedtime nudge speaks 1–2 hours before <strong>bedtime</strong>; the
            "hours until morning" sleep nudge counts down to <strong>wake-up</strong>.
            Nighttime starts at bedtime and ends at wake-up.
          </p>

          <Field
            label={`Wake-up — ${formatHour(wakeField.value)}`}
            htmlFor="wake"
          >
            <Slider
              id="wake"
              min={WAKE_HOUR.min}
              max={WAKE_HOUR.max}
              step={WAKE_HOUR.step}
              value={wakeField.value}
              onChange={wakeField.set}
              disabled={!isAdmin}
              aria-label="Wake-up hour"
            />
          </Field>

          <Field
            label={`Bedtime — ${formatHour(bedtimeField.value)}`}
            htmlFor="bedtime"
          >
            <Slider
              id="bedtime"
              min={BEDTIME_HOUR.min}
              max={BEDTIME_HOUR.max}
              step={BEDTIME_HOUR.step}
              value={bedtimeField.value}
              onChange={bedtimeField.set}
              disabled={!isAdmin}
              aria-label="Bedtime hour"
            />
          </Field>

          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={onResetVoice} disabled={!isAdmin}>
              Reset to defaults
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Time format</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="text-xs text-ink-muted">
            By default the button speaks times the everyday British way — "half
            past nine", "quarter to five". Turn this on to use the 24-hour form
            instead — "thirteen hundred hours", "seventeen thirty". The preview
            above updates as you change it.
          </p>
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-paper-border accent-accent-600"
              checked={use24Field.value}
              onChange={(e) => use24Field.set(e.target.checked)}
              disabled={!isAdmin}
            />
            <span>Use 24-hour time</span>
          </label>
        </CardBody>
      </Card>

      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        className="self-start text-sm text-accent-700 hover:underline"
        aria-expanded={showAdvanced}
      >
        {showAdvanced ? '▾ Fewer settings' : '▸ Advanced — customise phrases'}
      </button>

      {showAdvanced && (
      <>
      <Card>
        <CardHeader>
          <CardTitle>What the button says when pressed</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="text-xs text-ink-muted">
            Their name is filled in for you. Leave a box blank to use the default.
            Edits show in the preview above immediately.
          </p>
          <p className="text-xs text-ink-muted">
            The button also says the time of day on its own — "It's late morning.",
            "It's evening." — at the start of every press. That follows the clock,
            so it isn't something you set here.
          </p>
          {PHRASE_FIELDS_GENERAL.map((f) => (
            <PhraseInput
              key={f.key}
              field={f}
              wake={wakeField.value}
              bedtime={bedtimeField.value}
              value={phrasesShown[f.key]}
              elderName={account?.elderName ?? ''}
              onChange={(v) => {
                // Persist the storage-form string. Empty string is
                // *meaningful* — explicit silence — distinct from
                // `undefined` (never customised, fall back to the
                // default text in the input).
                setPhrase(f.key, v);
              }}
              onBlur={phrasesField.flush}
              disabled={!isAdmin}
            />
          ))}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Bedtime &amp; night-time</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="text-xs text-ink-muted">
            These speak relative to the wake-up and bedtime hours above. Bedtime nudges
            warn an hour or two before bed; the night-time messages count down to wake-up.
          </p>
          {PHRASE_FIELDS_TIME.map((f) => (
            <PhraseInput
              key={f.key}
              field={f}
              wake={wakeField.value}
              bedtime={bedtimeField.value}
              value={phrasesShown[f.key]}
              elderName={account?.elderName ?? ''}
              onChange={(v) => {
                setPhrase(f.key, v);
              }}
              onBlur={phrasesField.flush}
              disabled={!isAdmin}
            />
          ))}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
        </CardHeader>
        <CardBody className="space-y-6">
          <p className="text-xs text-ink-muted">
            A notification flashes the button and (unless you make it silent)
            speaks a short prompt on a loop until it is pressed. There are two
            kinds:
          </p>

          {/* Reminder notification — fires ahead of a calendar reminder. */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-ink">When a reminder pops up</h4>
            <PhraseInput
              field={NOTIFICATION_PROMPT_FIELD}
              wake={wakeField.value}
              bedtime={bedtimeField.value}
              value={phrasesShown.notificationPrompt}
              elderName={account?.elderName ?? ''}
              onChange={(v) => {
                setPhrase('notificationPrompt', v);
              }}
              onBlur={phrasesField.flush}
              disabled={!isAdmin}
            />
          </div>

          {/* Morning notification — optional wake-up nudge. */}
          <div className="space-y-3 border-t border-paper-border pt-4">
            <h4 className="text-sm font-semibold text-ink">Morning nudge</h4>
            <p className="text-xs text-ink-muted">
              When on, the button flashes (and optionally speaks the prompt) from
              wake-up until it is pressed to hear the day’s plan — or until two
              hours pass. A great nudge for anyone who doesn’t always remember to
              check the button each morning.
            </p>
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-paper-border accent-accent-600"
                checked={nudgeField.value}
                onChange={(e) => nudgeField.set(e.target.checked)}
                disabled={!isAdmin}
              />
              <span>Enable the morning notification</span>
            </label>
            {nudgeField.value && (
              <PhraseInput
                field={MORNING_PROMPT_FIELD}
                wake={wakeField.value}
                bedtime={bedtimeField.value}
                value={phrasesShown.morningPrompt}
                elderName={account?.elderName ?? ''}
                onChange={(v) => {
                  setPhrase('morningPrompt', v);
                }}
                onBlur={phrasesField.flush}
                disabled={!isAdmin}
              />
            )}
          </div>
        </CardBody>
      </Card>
      </>
      )}

      {/* Floating save-status pill. Purely informational — there is no Save
          button; each control saves itself. Must clear the bottom-right "ask
          me anything" launcher (AssistantWidget: fixed bottom-4 right-4 z-40):
          below xl it sits above the launcher (bottom-20), at xl flush
          (bottom-4). Errors stay until the next save attempt. */}
      {isAdmin && saveStatus.kind !== 'idle' && (
        <div className="pointer-events-none fixed inset-x-0 bottom-20 z-30 flex justify-center px-4 xl:bottom-4">
          <div
            role={saveStatus.kind === 'error' ? 'alert' : 'status'}
            aria-live={saveStatus.kind === 'error' ? 'assertive' : 'polite'}
            className="rounded-full border border-paper-border bg-white px-4 py-2 text-sm shadow-lg"
          >
            {saveStatus.kind === 'error' ? (
              <span className="text-red-700">
                Couldn't save — {saveStatus.message || 'try again'}
              </span>
            ) : saveStatus.kind === 'pending' ? (
              <span className="text-ink">Saving…</span>
            ) : (
              <span className="text-green-700">✓ Saved</span>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

function PhraseInput({
  field,
  wake,
  bedtime,
  value,
  elderName,
  onChange,
  onBlur,
  disabled,
}: {
  field: PhraseField;
  wake: number;
  bedtime: number;
  /** Saved value in storage form (with the {name} token). `null`/`undefined`
   * means "never customised" — the UI fills the input with the default text so
   * the caregiver sees exactly what the device says. Any string (including "")
   * is a deliberate value. */
  value: string | null | undefined;
  /** The elder's name, shown in place of the {name} token so caregivers never
   * see templating syntax. Edits are converted back to {name} on the way out. */
  elderName: string;
  /** Called with the storage-form string to persist ({name} token, not the
   * resolved name). "" persists as the silent state. We never coerce to
   * `undefined`/"use default" — "Reset to default" is an explicit action. */
  onChange: (next: string) => void;
  /** Leaving the box commits the in-flight edit immediately. */
  onBlur: () => void;
  disabled: boolean;
}) {
  const interval = field.intervalLabel(wake, bedtime);
  const labelWithInterval = `${field.label} · ${interval}`;
  const defaultText = field.placeholder;
  // `value` and `defaultText` are storage form (with {name}); compare and
  // reset against them, but show/edit the name-resolved form.
  const storedValue = value === null || value === undefined ? defaultText : value;
  const isAtDefault = storedValue === defaultText;
  const isSilent = storedValue === '';
  const displayValue = phraseToDisplay(storedValue, elderName);
  return (
    <Field label={labelWithInterval} htmlFor={`phrase-${field.key}`} hint={field.hint}>
      <Textarea
        id={`phrase-${field.key}`}
        rows={2}
        value={displayValue}
        onChange={(e) => onChange(phraseToStored(e.target.value, elderName))}
        onBlur={onBlur}
        maxLength={200}
        disabled={disabled}
      />
      <div className="mt-1 flex items-center justify-between gap-2 text-xs">
        <span className="text-ink-muted">
          {isSilent && '(Silent — no audio for this)'}
        </span>
        {!disabled && !isAtDefault && (
          <button
            type="button"
            onClick={() => onChange(defaultText)}
            className="text-accent-700 hover:underline"
          >
            Reset to default
          </button>
        )}
      </div>
    </Field>
  );
}

function hasAnyPhrase(p: Phrases): boolean {
  // Any key present means the caregiver has touched at least one
  // phrase — including empty-string values, which are deliberate
  // (explicit silence). `Object.keys` length is the right
  // discriminator now that empty-string is a meaningful state.
  return Object.keys(p).length > 0;
}

interface ActiveNotificationItem {
  eventTitle: string;
  minutesBefore: number;
  fireAt: string;
  spokenText: string;
  audioUrl: string | null;
  kind: 'event' | 'morning';
}

/** Annotation block surfacing the notification (event reminder or morning
 * nudge) that would be active at the preview moment. Shows the exact phrase the
 * device would speak (or a silent flash for an empty/cleared prompt). The
 * spoken text typically embeds the event title and time, so we don't repeat
 * them separately. */
function ActiveNotificationsBlock({
  notifications,
}: {
  notifications?: ActiveNotificationItem[];
}) {
  // The backend orders these by the device's play-priority, so the first is
  // the one it would actually voice; the rest are active too but the device
  // plays one at a time (the nearest event).
  const [playing, ...others] = notifications ?? [];
  if (!playing) return null;
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900">
      <p className="mb-1 text-xs font-bold">At this time, the button would:</p>
      {playing.spokenText
        ? <p className="italic">flash and speak: “{playing.spokenText}”</p>
        : <p className="italic text-amber-700">flash silently — no voice</p>}
      {others.length > 0 && (
        <p className="mt-2 text-xs text-amber-700">
          Also active: {others.map((r) => r.eventTitle).join(', ')} — the button plays just one at a time (the nearest event).
        </p>
      )}
    </div>
  );
}
